// services/searchService.js - Comprehensive search functionality
import { supabase, handleSupabaseError } from '../lib/supabase';
import { getBatchTeamStats } from './database';

/**
 * Wrapper for search operations with consistent error handling
 */
const searchOperation = async (operation, context) => {
  try {
    return await operation();
  } catch (error) {
    return handleSupabaseError(error, context);
  }
};

/**
 * Search for users/players by username or display name
 */
export const searchPlayers = async (query, limit = 10) => {
  return searchOperation(async () => {
    const trimmedQuery = query.trim();
    if (trimmedQuery.length < 2) {
      return { success: true, data: [] };
    }

    const { data, error } = await supabase
      .from('users')
      .select(`
        id, 
        username, 
        display_name, 
        avatar_url, 
        platform,
        gamer_tag,
        position,
        created_at
      `)
      .or(`username.ilike.%${trimmedQuery}%,display_name.ilike.%${trimmedQuery}%,gamer_tag.ilike.%${trimmedQuery}%`)
      .limit(limit)
      .order('display_name');

    if (error) throw error;

    // Get basic stats for each player
    const playersWithStats = await Promise.all(
      (data || []).map(async (player) => {
        try {
          const { data: stats } = await supabase
            .rpc('get_player_leaderboards')
            .eq('player_id', player.id)
            .single();

          return {
            id: player.id,
            type: 'player',
            title: player.display_name || player.username,
            subtitle: `@${player.username}${player.gamer_tag ? ` • ${player.gamer_tag}` : ''}`,
            avatar: player.avatar_url,
            platform: player.platform,
            position: player.position,
            stats: stats ? {
              goals: stats.total_goals || 0,
              assists: stats.total_assists || 0,
              matches: stats.matches_played || 0,
              wins: stats.wins || 0
            } : null,
            meta: {
              username: player.username,
              displayName: player.display_name,
              gamerTag: player.gamer_tag,
              createdAt: player.created_at
            }
          };
        } catch (error) {
          console.warn(`Failed to get stats for player ${player.id}:`, error);
          return {
            id: player.id,
            type: 'player',
            title: player.display_name || player.username,
            subtitle: `@${player.username}${player.gamer_tag ? ` • ${player.gamer_tag}` : ''}`,
            avatar: player.avatar_url,
            platform: player.platform,
            position: player.position,
            stats: null,
            meta: {
              username: player.username,
              displayName: player.display_name,
              gamerTag: player.gamer_tag,
              createdAt: player.created_at
            }
          };
        }
      })
    );

    return { success: true, data: playersWithStats };
  }, 'searching players');
};

/**
 * Search for teams by name or description - Optimized version
 */
export const searchTeams = async (query, limit = 10) => {
  return searchOperation(async () => {
    const trimmedQuery = query.trim();
    if (trimmedQuery.length < 2) {
      return { success: true, data: [] };
    }

    // Optimized query: Only fetch essential team data
    const { data, error } = await supabase
      .from('teams')
      .select(`
        id,
        name,
        description,
        logo,
        created_at,
        is_active,
        owner_id
      `)
      .or(`name.ilike.%${trimmedQuery}%,description.ilike.%${trimmedQuery}%`)
      .eq('is_active', true)
      .limit(limit)
      .order('name');

    if (error) throw error;

    // Get member count for each team in a single query
    const teamIds = (data || []).map(team => team.id);
    const { data: memberCounts, error: memberError } = await supabase
      .from('team_members')
      .select('team_id')
      .in('team_id', teamIds);

    if (memberError) {
      console.warn('Failed to get member counts:', memberError);
    }

    // Create member count map
    const memberCountMap = new Map();
    (memberCounts || []).forEach(member => {
      const count = memberCountMap.get(member.team_id) || 0;
      memberCountMap.set(member.team_id, count + 1);
    });

    // Transform to search result format without stats (lazy loading)
    const teamsWithBasicInfo = (data || []).map((team) => {
      const memberCount = memberCountMap.get(team.id) || 0;

      return {
        id: team.id,
        type: 'team',
        title: team.name,
        subtitle: `${memberCount} member${memberCount !== 1 ? 's' : ''}`,
        avatar: team.logo,
        description: team.description,
        stats: null, // Will be loaded lazily when needed
        meta: {
          ownerId: team.owner_id,
          memberCount,
          createdAt: team.created_at,
          isActive: team.is_active,
          // Lazy loading flags
          ownerLoaded: false,
          membersLoaded: false,
          statsLoaded: false
        }
      };
    });

    return { success: true, data: teamsWithBasicInfo };
  }, 'searching teams');
};

/**
 * Lazy load team owner information
 */
export const loadTeamOwner = async (ownerId) => {
  return searchOperation(async () => {
    if (!ownerId) return { success: true, data: null };

    const { data, error } = await supabase
      .from('users')
      .select('id, username, display_name, avatar_url')
      .eq('id', ownerId)
      .single();

    if (error) throw error;

    return { success: true, data };
  }, 'loading team owner');
};

/**
 * Batch load team owners for multiple owner IDs
 */
export const loadTeamOwnersBatch = async (ownerIds) => {
  return searchOperation(async () => {
    if (!ownerIds || ownerIds.length === 0) return { success: true, data: new Map() };

    const uniqueOwnerIds = [...new Set(ownerIds.filter(Boolean))];
    
    const { data, error } = await supabase
      .from('users')
      .select('id, username, display_name, avatar_url')
      .in('id', uniqueOwnerIds);

    if (error) throw error;

    // Create a map for easy lookup
    const ownerMap = new Map();
    (data || []).forEach(owner => {
      ownerMap.set(owner.id, owner);
    });

    return { success: true, data: ownerMap };
  }, 'loading team owners batch');
};

/**
 * Lazy load team members information
 */
export const loadTeamMembers = async (teamId) => {
  return searchOperation(async () => {
    if (!teamId) return { success: true, data: [] };

    const { data, error } = await supabase
      .from('team_members')
      .select(`
        role,
        user:users(id, username, display_name, avatar_url)
      `)
      .eq('team_id', teamId);

    if (error) throw error;

    return { success: true, data: data || [] };
  }, 'loading team members');
};

/**
 * Batch load team members for multiple teams
 */
export const loadTeamMembersBatch = async (teamIds) => {
  return searchOperation(async () => {
    if (!teamIds || teamIds.length === 0) return { success: true, data: new Map() };

    const uniqueTeamIds = [...new Set(teamIds.filter(Boolean))];
    
    const { data, error } = await supabase
      .from('team_members')
      .select(`
        team_id,
        role,
        user:users(id, username, display_name, avatar_url)
      `)
      .in('team_id', uniqueTeamIds);

    if (error) throw error;

    // Group members by team_id
    const membersMap = new Map();
    uniqueTeamIds.forEach(teamId => {
      membersMap.set(teamId, []);
    });
    
    (data || []).forEach(member => {
      const teamMembers = membersMap.get(member.team_id) || [];
      teamMembers.push({
        role: member.role,
        user: member.user
      });
      membersMap.set(member.team_id, teamMembers);
    });

    return { success: true, data: membersMap };
  }, 'loading team members batch');
};

/**
 * Lazy load team stats
 */
export const loadTeamStats = async (teamId) => {
  return searchOperation(async () => {
    if (!teamId) return { success: true, data: null };

    const teamStatsMap = await getBatchTeamStats([teamId]);
    const stats = teamStatsMap.get(teamId);

    return { 
      success: true, 
      data: stats ? {
        wins: stats.match_wins || 0,
        losses: stats.match_losses || 0,
        tournaments: stats.tournaments_won || 0,
        goals: stats.total_team_goals || 0
      } : null 
    };
  }, 'loading team stats');
};

/**
 * Search for tournaments by name or description
 */
export const searchTournaments = async (query, limit = 10) => {
  return searchOperation(async () => {
    const trimmedQuery = query.trim();
    if (trimmedQuery.length < 2) {
      return { success: true, data: [] };
    }

    const { data, error } = await supabase
      .from('tournaments')
      .select(`
        id,
        name,
        description,
        logo,
        format,
        platform,
        status,
        max_teams,
        min_teams,
        prize_pool,
        start_date,
        start_time,
        created_at,
        bracket_generated,
        created_by:users(username, display_name, avatar_url),
        tournament_registrations(
          team:teams(id, name, logo)
        )
      `)
      .or(`name.ilike.%${trimmedQuery}%,description.ilike.%${trimmedQuery}%`)
      .limit(limit)
      .order('created_at', { ascending: false });

    if (error) throw error;

    const transformedTournaments = (data || []).map(tournament => {
      const registeredCount = tournament.tournament_registrations?.length || 0;
      const formatMap = {
        'single_elimination': 'Single Elimination',
        'round_robin': 'Round Robin',
        'bo3': 'Best of 3',
        'bo5': 'Best of 5',
        'league': 'League',
        'versus': 'Versus',
        'group_stage_knockout': 'Group Stage + Knockout'
      };

      const formatDisplay = formatMap[tournament.format] || tournament.format;
      const statusMap = {
        'open': 'Open for Registration',
        'in_progress': 'In Progress',
        'completed': 'Completed',
        'cancelled': 'Cancelled'
      };

      const statusDisplay = statusMap[tournament.status] || tournament.status;
      const creatorName = tournament.created_by?.display_name || tournament.created_by?.username || 'Admin';

      return {
        id: tournament.id,
        type: 'tournament',
        title: tournament.name,
        subtitle: `${formatDisplay} • ${statusDisplay} • ${registeredCount}/${tournament.max_teams} teams`,
        avatar: tournament.logo, // Use actual tournament logo
        description: tournament.description,
        stats: {
          registeredTeams: registeredCount,
          maxTeams: tournament.max_teams,
          status: tournament.status,
          prize: tournament.prize_pool || '$0'
        },
        meta: {
          format: tournament.format,
          formatDisplay,
          platform: tournament.platform,
          status: tournament.status,
          statusDisplay,
          startDate: tournament.start_date,
          startTime: tournament.start_time,
          createdBy: creatorName,
          bracketGenerated: tournament.bracket_generated,
          createdAt: tournament.created_at,
          prizePool: tournament.prize_pool,
          logo: tournament.logo
        }
      };
    });

    return { success: true, data: transformedTournaments };
  }, 'searching tournaments');
};

/**
 * Comprehensive search across all types
 */
export const searchAll = async (query, options = {}) => {
  const { 
    includeUsers = true, 
    includeTeams = true, 
    includeTournaments = true,
    limit = 5 
  } = options;

  return searchOperation(async () => {
    const trimmedQuery = query.trim();
    if (trimmedQuery.length < 2) {
      return { 
        success: true, 
        data: { 
          players: [], 
          teams: [], 
          tournaments: [], 
          total: 0 
        } 
      };
    }

    const searchPromises = [];
    
    if (includeUsers) {
      searchPromises.push(searchPlayers(trimmedQuery, limit));
    } else {
      searchPromises.push(Promise.resolve({ success: true, data: [] }));
    }
    
    if (includeTeams) {
      searchPromises.push(searchTeams(trimmedQuery, limit));
    } else {
      searchPromises.push(Promise.resolve({ success: true, data: [] }));
    }
    
    if (includeTournaments) {
      searchPromises.push(searchTournaments(trimmedQuery, limit));
    } else {
      searchPromises.push(Promise.resolve({ success: true, data: [] }));
    }

    const [playersResult, teamsResult, tournamentsResult] = await Promise.all(searchPromises);

    // Check for errors
    if (!playersResult.success) throw new Error(playersResult.error);
    if (!teamsResult.success) throw new Error(teamsResult.error);
    if (!tournamentsResult.success) throw new Error(tournamentsResult.error);

    const players = playersResult.data || [];
    const teams = teamsResult.data || [];
    const tournaments = tournamentsResult.data || [];

    return {
      success: true,
      data: {
        players,
        teams,
        tournaments,
        total: players.length + teams.length + tournaments.length,
        combined: [
          ...players.map(item => ({ ...item, category: 'Players' })),
          ...teams.map(item => ({ ...item, category: 'Teams' })),
          ...tournaments.map(item => ({ ...item, category: 'Tournaments' }))
        ].sort((a, b) => {
          // Sort by relevance (exact matches first, then alphabetical)
          const aTitle = a.title.toLowerCase();
          const bTitle = b.title.toLowerCase();
          const queryLower = trimmedQuery.toLowerCase();
          
          const aExact = aTitle.includes(queryLower);
          const bExact = bTitle.includes(queryLower);
          
          if (aExact && !bExact) return -1;
          if (!aExact && bExact) return 1;
          
          return aTitle.localeCompare(bTitle);
        })
      }
    };
  }, 'performing comprehensive search');
};

/**
 * Get search suggestions based on partial query
 */
export const getSearchSuggestions = async (query, limit = 8) => {
  return searchOperation(async () => {
    const trimmedQuery = query.trim();
    if (trimmedQuery.length < 1) {
      return { success: true, data: [] };
    }

    // Get quick suggestions from multiple sources
    const [usersResult, teamsResult, tournamentsResult] = await Promise.all([
      supabase
        .from('users')
        .select('username, display_name')
        .or(`username.ilike.${trimmedQuery}%,display_name.ilike.${trimmedQuery}%`)
        .limit(3),
      supabase
        .from('teams')
        .select('name')
        .ilike('name', `${trimmedQuery}%`)
        .eq('is_active', true)
        .limit(3),
      supabase
        .from('tournaments')
        .select('name')
        .ilike('name', `${trimmedQuery}%`)
        .limit(2)
    ]);

    const suggestions = [];

    // Add user suggestions
    if (usersResult.data) {
      usersResult.data.forEach(user => {
        const name = user.display_name || user.username;
        if (name.toLowerCase().startsWith(trimmedQuery.toLowerCase())) {
          suggestions.push({
            text: name,
            type: 'player',
            icon: '👤'
          });
        }
      });
    }

    // Add team suggestions
    if (teamsResult.data) {
      teamsResult.data.forEach(team => {
        if (team.name.toLowerCase().startsWith(trimmedQuery.toLowerCase())) {
          suggestions.push({
            text: team.name,
            type: 'team',
            icon: '👥'
          });
        }
      });
    }

    // Add tournament suggestions
    if (tournamentsResult.data) {
      tournamentsResult.data.forEach(tournament => {
        if (tournament.name.toLowerCase().startsWith(trimmedQuery.toLowerCase())) {
          suggestions.push({
            text: tournament.name,
            type: 'tournament',
            icon: '🏆'
          });
        }
      });
    }

    return { 
      success: true, 
      data: suggestions.slice(0, limit) 
    };
  }, 'getting search suggestions');
};

const searchService = {
  searchPlayers,
  searchTeams,
  searchTournaments,
  searchAll,
  getSearchSuggestions,
  loadTeamOwner,
  loadTeamOwnersBatch,
  loadTeamMembers,
  loadTeamMembersBatch,
  loadTeamStats
};

export default searchService;
